import { useEffect, useId, useRef } from 'react'

/** Screen pixels between a button and the panel below it, and from the window's edge. */
const GAP = 6
const EDGE = 8

/**
 * Places a native popover panel below the button that opens it, or above it where the panel fits only
 * there (the sidebar's foot), aligned to the button's start or end edge and kept inside the window on
 * both sides, also while the window is resized or the toolbar scrolls. Popovers open in the top layer,
 * so CSS alone cannot anchor them in every browser yet. `trigger` and `popover` go on the button and the
 * panel; `id` names a panel that other buttons open too.
 */
export function useAnchoredPopover<Panel extends HTMLElement>(align: 'start' | 'end' = 'start', id?: string) {
  const own = useId()
  const panelId = id ?? own
  const button = useRef<HTMLButtonElement>(null)
  const panel = useRef<Panel>(null)
  useEffect(() => {
    const element = panel.current
    if (!element) return
    let frame = 0
    const place = () => {
      if (!button.current) return
      const anchor = button.current.getBoundingClientRect()
      const { clientWidth, clientHeight } = document.documentElement
      // Measured at the top left, where the panel has the whole window to take its natural size.
      Object.assign(element.style, { right: 'auto', bottom: 'auto', left: '0px', top: '0px', maxHeight: 'none' })
      const width = element.offsetWidth
      const left = align === 'start' ? anchor.left : anchor.right - width
      element.style.left = `${Math.max(EDGE, Math.min(left, clientWidth - EDGE - width))}px`
      const below = clientHeight - anchor.bottom - GAP - EDGE
      const above = anchor.top - GAP - EDGE
      if (element.offsetHeight > below && above > below) {
        // Held by its lower edge, so a panel whose content grows grows away from the button.
        element.style.top = 'auto'
        element.style.bottom = `${clientHeight - anchor.top + GAP}px`
        element.style.maxHeight = `${above}px`
      } else {
        element.style.top = `${anchor.bottom + GAP}px`
        element.style.maxHeight = `${below}px`
      }
    }
    // Before the next paint, so the panel never shows at the wrong place.
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(place)
    }
    // Scrolling the panel's own content does not move its button.
    const scrolled = (event: Event) => {
      if (!element.contains(event.target as Node)) schedule()
    }
    const stop = () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', scrolled, true)
    }
    const toggle = (event: Event) => {
      if ((event as ToggleEvent).newState !== 'open') return stop()
      schedule()
      window.addEventListener('resize', schedule)
      window.addEventListener('scroll', scrolled, true)
    }
    element.addEventListener('beforetoggle', toggle)
    return () => {
      element.removeEventListener('beforetoggle', toggle)
      stop()
    }
  }, [align])
  return {
    button,
    panel,
    trigger: { ref: button, popoverTarget: panelId },
    popover: { ref: panel, id: panelId, popover: 'auto' as const },
  }
}
